import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { Bound, deserializeXYWH } from '@blocksuite/global/gfx';
import { computed, effect, signal } from '@preact/signals-core';
import { nothing } from 'lit';

import type { BlockService } from '../../extension/index.js';
import { GfxControllerIdentifier } from '../../gfx/identifiers.js';
import type {
  BoxSelectionContext,
  DragEndContext,
  DragMoveContext,
  GfxViewTransformInterface,
} from '../../gfx/interactivity/index.js';
import type { GfxBlockElementModel } from '../../gfx/model/gfx-block-model.js';
import { measureGfxPerformance } from '../../gfx/performance.js';
import {
  cancelGfxViewportFrame,
  scheduleGfxViewportFrame,
} from '../../gfx/viewport-frame.js';
import { SurfaceSelection } from '../../selection/index.js';
import { BlockComponent } from './block-component.js';

export function isGfxBlockComponent(
  element: unknown
): element is GfxBlockComponent {
  return (element as GfxBlockComponent)?.[GfxElementSymbol] === true;
}

export const GfxElementSymbol = Symbol('GfxElement');

export type GfxBlockElementSize = {
  height: number;
  width: number;
};

export function getGfxBlockResizeObserverEntryBorderBoxSize(
  entry: ResizeObserverEntry
): GfxBlockElementSize {
  const borderBoxSize = Array.isArray(entry.borderBoxSize)
    ? entry.borderBoxSize[0]
    : entry.borderBoxSize;

  return {
    height: borderBoxSize?.blockSize ?? entry.contentRect.height,
    width: borderBoxSize?.inlineSize ?? entry.contentRect.width,
  };
}

/**
 * Canonical size reader for persisted GFX block geometry.
 *
 * This intentionally reads untransformed border-box dimensions
 * (`ResizeObserverEntry.borderBoxSize` or `offsetHeight/offsetWidth`) and
 * returns values in model coordinates. Canvas autosizers must not reintroduce
 * `getBoundingClientRect() / viewport.zoom` at call sites; that mixes viewport
 * transforms into persisted `xywh` and causes resize flicker/clipped frames.
 *
 * If a caller owns an additional model-space scale, pass it through
 * `options.scale`. Any semantic change here affects database, automation,
 * ELSTER, Schulung, and edgeless-text canvas sizing and needs a full
 * canvas autosize audit.
 */
export function getGfxBlockElementModelSize(
  element: HTMLElement,
  options: {
    resizeObserverEntry?: ResizeObserverEntry;
    scale?: number;
  } = {}
): GfxBlockElementSize {
  const size = options.resizeObserverEntry
    ? getGfxBlockResizeObserverEntryBorderBoxSize(options.resizeObserverEntry)
    : {
        height: element.offsetHeight,
        width: element.offsetWidth,
      };
  const scale = options.scale ?? 1;

  return {
    height: size.height * scale,
    width: size.width * scale,
  };
}

function persistFinalDragBound(
  model: GfxBlockElementModel,
  currentBound: Bound
) {
  const finalXYWH = currentBound.serialize();
  model.pop('xywh');
  if (model.xywh !== finalXYWH) {
    model.xywh = finalXYWH;
  }
}

function getRenderXYWH(model: GfxBlockElementModel) {
  return model.transientXYWH ?? model.xywh;
}

function getGfxCSSTransform(instance: GfxBlockComponent) {
  const viewport = instance.gfx.viewport;
  const { translateX, translateY, zoom } = viewport;
  const bound = Bound.deserialize(getRenderXYWH(instance.model));

  const scaledX = bound.x * zoom;
  const scaledY = bound.y * zoom;
  const deltaX = scaledX - bound.x;
  const deltaY = scaledY - bound.y;

  return `translate(${translateX + deltaX}px, ${translateY + deltaY}px) scale(${zoom})`;
}

function getGfxRenderingRect(instance: GfxBlockComponent) {
  const { xywh$ } = instance.model;

  if (!xywh$) {
    throw new BlockSuiteError(
      ErrorCode.GfxBlockElementError,
      `Error on rendering '${instance.model.flavour}': Gfx block's model should have 'xywh' property.`
    );
  }

  const [x, y, w, h] = deserializeXYWH(
    instance.model.transientXYWH ?? xywh$.value
  );

  return { x, y, w, h, zIndex: instance.toZIndex() };
}

function updateTransform(element: GfxBlockComponent) {
  if (element.transformState$.value === 'idle') return;
  if (element.dataset.streamlainPerformanceSuspend === 'true') return;

  measureGfxPerformance(
    element.std,
    'viewport-dom-transform',
    () => {
      const { viewport } = element.gfx;
      element.viewportState = viewport.serializeRecord();
      element.style.transformOrigin = '0 0';
      element.style.transform = element.getCSSTransform();
    },
    undefined,
    () => ({
      blockId: element.model.id,
      flavour: element.model.flavour,
      tagName: element.tagName.toLowerCase(),
      transformState: element.transformState$.value,
      modelXYWH: getRenderXYWH(element.model),
      cssTransform: element.style.transform,
      viewportState: element.viewportState ?? null,
    })
  );
}

export function createGfxBlockViewportTransformScheduler(
  element: Pick<GfxBlockComponent, 'isConnected'>,
  update: () => void,
  scope: object = element
) {
  const request = () => {
    scheduleGfxViewportFrame(scope, 'dom', element, () => {
      if (element.isConnected) update();
    });
  };

  const cancel = () => {
    cancelGfxViewportFrame(scope, 'dom', element);
  };

  return { cancel, request };
}

function updateRenderingRect(element: GfxBlockComponent) {
  const { x, y, w, h, zIndex } = element.getRenderingRect();

  if (element.style.left !== `${x}px`) element.style.left = `${x}px`;
  if (element.style.top !== `${y}px`) element.style.top = `${y}px`;
  if (element.style.width !== `${w}px`) element.style.width = `${w}px`;
  if (element.style.height !== `${h}px`) element.style.height = `${h}px`;
  if (element.style.zIndex !== zIndex) element.style.zIndex = zIndex;
}

function updateBlockVisibility(view: GfxBlockComponent) {
  if (view.transformState$.value === 'active') {
    view.style.visibility = 'visible';
    view.style.pointerEvents = 'auto';
    view.classList.remove('block-idle');
    view.classList.add('block-active');
  } else {
    view.style.visibility = 'hidden';
    view.style.pointerEvents = 'none';
    view.classList.remove('block-active');
    view.classList.add('block-idle');
  }
}

function handleGfxConnection(instance: GfxBlockComponent) {
  instance.style.position = 'absolute';

  // Surface primitives and selection/presence chrome apply viewport changes
  // on the next animation frame. Keep DOM-backed GFX blocks on that same
  // paint boundary; updating them synchronously makes images jump ahead of
  // their canvas areas and selection frames during smooth camera movement.
  const viewportTransform = createGfxBlockViewportTransformScheduler(
    instance,
    () => instance.refreshGfxTransform(),
    instance.std
  );

  instance.disposables.add(
    instance.gfx.viewport.viewportUpdated.subscribe(() => {
      viewportTransform.request();
    })
  );
  instance.disposables.add(viewportTransform.cancel);

  instance.disposables.add(
    instance.gfx.layer.slots.layerUpdated.subscribe(() => {
      // Layer rebuilds can be triggered by surface primitives hydrating after
      // block DOM nodes. The block itself has no prop update in that case, but
      // its stacking position may still have changed.
      updateRenderingRect(instance);
    })
  );

  instance.disposables.add(
    instance.store.slots.blockUpdated.subscribe(({ type, id }) => {
      if (id === instance.model.id && type === 'update') {
        updateTransform(instance);
      }
    })
  );

  instance.disposables.add(
    effect(() => {
      updateBlockVisibility(instance);
      updateTransform(instance);
    })
  );
}

function renderUpdatedGfxBlock(instance: GfxBlockComponent) {
  updateRenderingRect(instance);
  updateTransform(instance);

  return instance.renderGfxBlock();
}

async function scheduleParentGfxUpdate(
  instance: Pick<GfxBlockComponent, 'hasUpdated' | 'model' | 'parentElement'>
) {
  const parent = instance.parentElement;

  if (instance.hasUpdated || !parent || !('scheduleUpdateChildren' in parent)) {
    return;
  }

  await (parent.scheduleUpdateChildren as (id: string) => Promise<void>)(
    instance.model.id
  );
}

async function scheduleGfxBlockUpdate(
  instance: Pick<GfxBlockComponent, 'hasUpdated' | 'model' | 'parentElement'>,
  scheduleSuperUpdate: () => void | Promise<unknown>
) {
  await scheduleParentGfxUpdate(instance);

  return scheduleSuperUpdate();
}

function resolveConnectedGfxBlockState<Model extends GfxBlockElementModel>(
  instance: GfxBlockComponent<Model>
) {
  const std = instance.std;
  const store = instance.store;
  const blockId = instance.blockId;
  if (!std || !store || !blockId) return null;

  const gfx = std.getOptional(GfxControllerIdentifier);
  const model = store.getModelById<Model>(blockId);
  if (!gfx || !model) return null;

  return { gfx, model };
}

function resolveConnectedGfxBlockZIndex<Model extends GfxBlockElementModel>(
  instance: GfxBlockComponent<Model>
): string {
  const state = resolveConnectedGfxBlockState(instance);
  if (!state) return '0';
  return state.gfx.layer.getZIndex(state.model).toString() ?? '0';
}

export abstract class GfxBlockComponent<
  Model extends GfxBlockElementModel = GfxBlockElementModel,
  Service extends BlockService = BlockService,
  WidgetName extends string = string,
>
  extends BlockComponent<Model, Service, WidgetName>
  implements GfxViewTransformInterface
{
  [GfxElementSymbol] = true;

  readonly transformState$ = signal<'idle' | 'active'>('active');

  viewportState?: string;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    handleGfxConnection(this);
  }

  onDragMove = ({ dx, dy, currentBound }: DragMoveContext) => {
    this.model.xywh = currentBound.moveDelta(dx, dy).serialize();
  };

  onDragStart() {
    this.model.stash('xywh');
  }

  onDragEnd({ currentBound }: DragEndContext) {
    persistFinalDragBound(this.model, currentBound);
  }

  onBoxSelected(_: BoxSelectionContext) {}

  refreshGfxTransform() {
    updateRenderingRect(this);
    updateTransform(this);
  }

  getCSSTransform() {
    return getGfxCSSTransform(this);
  }

  getRenderingRect() {
    return getGfxRenderingRect(this);
  }

  override renderBlock() {
    return renderUpdatedGfxBlock(this);
  }

  renderGfxBlock(): unknown {
    return nothing;
  }

  renderPageContent(): unknown {
    return nothing;
  }

  override async scheduleUpdate() {
    return scheduleGfxBlockUpdate(this, () => super.scheduleUpdate());
  }

  toZIndex(): string {
    return resolveConnectedGfxBlockZIndex(this);
  }

  updateZIndex(): void {
    this.style.zIndex = this.toZIndex();
  }
}

export function toGfxBlockComponent<
  Model extends GfxBlockElementModel,
  Service extends BlockService,
  WidgetName extends string,
  B extends typeof BlockComponent<Model, Service, WidgetName>,
>(CustomBlock: B) {
  // @ts-expect-error ignore
  return class extends CustomBlock {
    [GfxElementSymbol] = true;

    readonly transformState$ = signal<'idle' | 'active'>('active');

    viewportState?: string;

    override selected$ = computed(() => {
      const selection = this.std.selection.value.find(
        selection => selection.blockId === this.model?.id
      );
      if (!selection) return false;
      return selection.is(SurfaceSelection);
    });

    onDragMove({ dx, dy, currentBound }: DragMoveContext) {
      this.model.xywh = currentBound.moveDelta(dx, dy).serialize();
    }

    onDragStart() {
      this.model.stash('xywh');
    }

    onDragEnd({ currentBound }: DragEndContext) {
      persistFinalDragBound(this.model, currentBound);
    }

    onBoxSelected(_: BoxSelectionContext) {}

    refreshGfxTransform() {
      updateRenderingRect(this);
      updateTransform(this);
    }

    get gfx() {
      return this.std.get(GfxControllerIdentifier);
    }

    override connectedCallback(): void {
      super.connectedCallback();
      handleGfxConnection(this);
    }

    getCSSTransform() {
      return getGfxCSSTransform(this as GfxBlockComponent);
    }

    getRenderingRect(): {
      x: number;
      y: number;
      w: number;
      h: number;
      zIndex: string;
    } {
      return getGfxRenderingRect(this as GfxBlockComponent);
    }

    override renderBlock() {
      return renderUpdatedGfxBlock(this as GfxBlockComponent);
    }

    renderGfxBlock(): unknown {
      return this.renderPageContent();
    }

    renderPageContent() {
      return super.renderBlock();
    }

    override async scheduleUpdate() {
      return scheduleGfxBlockUpdate(this as GfxBlockComponent, () =>
        super.scheduleUpdate()
      );
    }

    toZIndex(): string {
      return resolveConnectedGfxBlockZIndex(this);
    }

    updateZIndex(): void {
      this.style.zIndex = this.toZIndex();
    }
  } as B & {
    new (...args: any[]): GfxBlockComponent;
  };
}
